import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * `next/link`'s call-site shape over React Router's `Link`.
 *
 * The delivered console used `next/link` in 27 files. Keeping the `href` prop
 * (rather than React Router's `to`) makes the SPA port a mechanical import
 * rewrite, so the diff answers "did this change behaviour?" on its face.
 *
 * The escape hatch to a plain anchor is narrower than it looks. React Router's
 * own Link already declines to intercept a scheme-qualified or protocol-relative
 * URL, so `https://…` and `mailto:…` would be fine without it — MEASURED, by
 * mutating this predicate to `false` and watching only one case fail. What it
 * genuinely protects is the bare FRAGMENT: React Router reads `#section` as a
 * relative path and pushes it, replacing the page with nothing. The broader
 * predicate is kept because stating the rule beats depending on where another
 * library happens to draw the same line.
 */
export type LinkProps = Omit<React.ComponentPropsWithoutRef<"a">, "href"> & {
  href: string;
  replace?: boolean;
  prefetch?: boolean | null;
  scroll?: boolean;
};

function isExternal(href: string): boolean {
  return /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href) || href.startsWith("#");
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, replace, prefetch: _prefetch, scroll: _scroll, children, ...rest },
  ref,
) {
  if (isExternal(href)) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <RouterLink ref={ref} to={href} replace={replace} {...rest}>
      {children}
    </RouterLink>
  );
});

export default Link;
